import { useEffect, useRef, useState } from "react";
import { Route, Routes as RouterRoutes, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { Header, SimpleSidebar } from "../components/ui";
import { ModuleError } from "../components/ErrorBoundary";
import ReleaseUpdateBanner from "../components/ui/ReleaseUpdateBanner";
import useSidebarOpen from "../hooks/useSidebarOpen";
import ModuleGrid from "../components/ui/ModuleNavigation/ModuleGrid";
import Admin from "../modules/Admin";
import Email from "../modules/Email";
import Contact from "../modules/Contact";
import Artwork from "../modules/Artwork";
import Instance from "../modules/Instance";
import Invoice from "../modules/Invoice";
import Consignment from "../modules/Consignment";
import Offer from "../modules/Offer";
import SelectLists from "../modules/SelectList";
import Shipment from "../modules/Shipment";
import Warehouse from "../modules/Warehouse";
import Jobs from "../modules/Jobs";
import Reporting from "../modules/Reporting";
import PdfGeneratorSection from "../modules/Reporting/PdfGeneratorSection";
import Reports from "../modules/Reports";
import Artist from "../modules/Artist";
import Company from "../modules/Company";
import Exhibition from "../modules/Exhibition";
import Reservation from "../modules/Reservation";
import Sites from "../modules/Sites";

const routes = [
  {
    path: "admin/*",
    element: Admin,
  },
  {
    path: "email/*",
    element: Email,
  },
  {
    path: "contacts/*",
    element: Contact,
  },
  {
    path: "companies/*",
    element: Company,
  },
  {
    path: "artworks/*",
    element: Artwork,
  },
  {
    path: "instance/*",
    element: Instance,
  },
  {
    path: "invoice/*",
    element: Invoice,
  },
  {
    path: "consignment/*",
    element: Consignment,
  },
  {
    path: "offer/*",
    element: Offer,
  },
  {
    path: "shipments/*",
    element: Shipment,
  },
  {
    path: "warehouse/*",
    element: Warehouse,
  },
  {
    path: "sites/*",
    element: Sites,
  },
  {
    path: "jobs/*",
    element: Jobs,
  },
  {
    path: "reports/*",
    element: Reports,
  },
  {
    path: "reporting/*",
    element: Reporting,
  },
  {
    path: "pdf-generator/*",
    element: PdfGeneratorSection,
  },
  {
    path: "*",
    element: ModuleGrid,
  },
  {
    path: "select-lists/*",
    element: SelectLists,
  },
  {
    path: "artist/*",
    element: Artist,
  },
  {
    path: "exhibition/*",
    element: Exhibition,
  },
  {
    path: "reservations/*",
    element: Reservation,
  },
];

const Auth = () => {
  const [open, setOpen] = useSidebarOpen();
  const [hasSidebarEntries, setHasSidebarEntries] = useState(false);
  const location = useLocation();
  const { pathname } = location || {};
  const paths = pathname?.split("/");
  const path = paths[paths?.length - 1];

  // The scrollable body lives outside the route switch, so its scroll position
  // would otherwise persist across navigations (a new detail page would open
  // already scrolled to wherever the previous page was). Reset it to the top
  // whenever the path changes. Keyed on pathname only, so query-string changes
  // (filters, pagination) leave the scroll position alone.
  const scrollContainerRef = useRef(null);
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
    if (window.matchMedia("(max-width: 1023px)").matches) {
      setOpen(false);
    }
  }, [pathname, setOpen]);

  const headerProps = {
    open,
    setOpen,
    showNavigationToggle: hasSidebarEntries,
  };

  const bodyProps = {
    open,
    setOpen,
  };

  const simpleSideBar = {
    onEntriesChange: setHasSidebarEntries,
    open,
    setOpen,
    path,
  };

  return (
    <div className="flex h-[100dvh] w-full overflow-hidden">
      <SimpleSidebar {...simpleSideBar} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header {...headerProps} />
        <ReleaseUpdateBanner />
        <div
          ref={scrollContainerRef}
          className="min-h-0 w-full flex-1 overflow-x-hidden overflow-y-auto"
        >
          <Sentry.ErrorBoundary
            fallback={ModuleError}
            beforeCapture={scope => {
              scope.setTag("module", "auth-routes");
            }}
          >
            <RouterRoutes>
              {routes?.map((item, index) => {
                return (
                  <Route
                    key={index}
                    path={item?.path}
                    element={<item.element {...bodyProps} />}
                  />
                );
              })}
            </RouterRoutes>
          </Sentry.ErrorBoundary>
        </div>
      </div>
    </div>
  );
};

export default Auth;
